// Mock data for ElevateAI landing page

export const brand = {
  name: 'ElevateAI',
  email: 'hello@elevatenext.ai',
  tagline: 'Turn AI Into Your Unfair _Advantage',
  linkedin: 'https://www.linkedin.com/in/sudheervazrapu',
  calendarUrl: 'https://cal.com/sudheer-ai/30min',
};

export const navLinks = [
  { label: 'Services', href: '/#services' },
  { label: 'AIOS', href: '/aios' },
  { label: 'Approach', href: '/#approach' },
  { label: 'Education', href: '/education' },
  { label: 'Blog', href: '/#blog' },
];

export const socialLinks = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/sudheervazrapu', external: true },
  { label: 'Newsletter', href: '#newsletter' },
];

// ---------------- Education page content ----------------
export const educationHero = {
  eyebrow: 'AI Education',
  title: 'Upskill Your Team. Actually.',
  description:
    'No theory. No buzzwords. Hands-on workshops that teach your people to think AI-first and use it in their actual workflows starting Monday.',
  primaryCta: { label: 'Design Your Training', href: '/#contact' },
  secondaryCta: { label: 'See Sample Curriculum', href: '#curriculum' },
};

export const trainingProblems = [
  {
    title: 'Generic Webinars Don\u2019t Stick',
    description:
      'Your team watches a 1-hour webinar about ChatGPT, nods along, then goes back to doing things the old way.',
  },
  {
    title: 'Theory Without Practice Is Useless',
    description:
      'Understanding how transformers work doesn\u2019t help your sales team write better follow-up emails.',
  },
  {
    title: 'One-Size-Fits-All Fails',
    description:
      'Marketing needs different AI skills than finance. Customer success needs different tools than HR.',
  },
  {
    title: 'No Follow-Through',
    description:
      'Without ongoing support and real use cases, people forget what they learned within 2 weeks.',
  },
];

export const trainingApproach = [
  {
    title: 'Hands-On, Not Theoretical',
    description:
      "Every session includes live exercises using your company's actual data, tools, and workflows. We teach by doing, not lecturing.",
  },
  {
    title: 'Department-Specific Training',
    description:
      'Sales learns different AI skills than operations. Marketing different than finance. We design custom tracks for each team.',
  },
  {
    title: 'Ongoing Support',
    description:
      "Training isn't one-and-done. We provide follow-up sessions, office hours, and a resource library so learning continues.",
  },
];

export const trainingFormats = [
  {
    duration: 'Half-day',
    audience: 'C-suite, VPs, Directors',
    title: 'Executive Workshop',
    description:
      'AI strategy overview, ROI frameworks, and how to evaluate AI opportunities. Designed for decision-makers who need to understand without becoming practitioners.',
    outcome:
      'Leadership can confidently evaluate AI investments and set strategic direction',
  },
  {
    duration: 'Full-day',
    audience: 'Sales, Marketing, Ops, CS',
    title: 'Department Deep-Dive',
    description:
      'Department-specific AI workflows and tools. Each team learns how to automate their most time-consuming tasks using AI relevant to their function.',
    outcome: 'Teams start using AI in daily work within 1 week',
  },
  {
    duration: '2-3 days',
    audience: 'Product, Engineering, Data',
    title: 'Technical Bootcamp',
    description:
      'AI engineering fundamentals, prompt engineering, agent design, and integration patterns. For teams building or maintaining AI systems.',
    outcome: 'Technical teams can build and deploy AI agents independently',
  },
  {
    duration: '2-8 weeks',
    audience: 'Entire organization',
    title: 'Custom Curriculum',
    description:
      'Phased rollout across all departments with role-specific training, change management, and ongoing support. Full organizational AI transformation.',
    outcome: 'Company-wide AI adoption with measurable productivity gains',
  },
];

export const curriculumCore = [
  'How to write effective prompts that get usable outputs',
  'When to use AI vs. when to use traditional tools',
  'How to spot AI hallucinations and verify outputs',
  'Data privacy and security basics for AI usage',
  'How to evaluate AI tools for your specific needs',
  'Workflow redesign: automating manual handoffs',
];

export const curriculumRoles = [
  { role: 'Sales', detail: 'AI-powered email sequences and meeting prep' },
  { role: 'Marketing', detail: 'Content generation and campaign automation' },
  { role: 'Operations', detail: 'Process mapping and workflow automation' },
  { role: 'Finance', detail: 'Data extraction and reporting automation' },
  { role: 'Customer Success', detail: 'Response automation and sentiment analysis' },
  { role: 'HR', detail: 'Candidate screening and onboarding workflows' },
];

export const audienceFit = [
  {
    title: 'Teams That Tried AI and Failed',
    description:
      'You rolled out ChatGPT licenses. Nobody used them. Or people tried and got garbage outputs. We fix this with proper training.',
  },
  {
    title: 'Growing Companies Hiring Fast',
    description:
      "You're scaling headcount and need new hires to be AI-proficient from day one. We build AI training into your onboarding.",
  },
  {
    title: 'Leaders Who Know They\u2019re Behind',
    description:
      'You see competitors moving faster and know AI is part of it. You need your team upskilled but don\u2019t know where to start.',
  },
];

export const trainingProcess = [
  { number: '01', title: 'Assessment', description: 'We audit your current AI usage and identify skill gaps.', meta: 'Week 1' },
  { number: '02', title: 'Curriculum Design', description: 'We design role-specific training tracks using your data.', meta: 'Week 2' },
  { number: '03', title: 'Training Delivery', description: 'Hands-on workshops delivered on-site or virtually.', meta: 'Week 3-4' },
  { number: '04', title: 'Ongoing Support', description: 'Office hours, resource library, and follow-up sessions.', meta: 'Ongoing' },
];

export const services = [
  {
    id: 'agents',
    title: 'AI Agents',
    description:
      'Custom autonomous agents that handle your busywork \u2014 from lead gen to customer support \u2014 so you can focus on growth.',
    visual: 'agents',
  },
  {
    id: 'workflows',
    title: 'n8n Workflows',
    description:
      'Automated workflows connecting 400+ apps that save 10+ hours per week. No more manual data entry or copy-pasting between tools.',
    visual: 'workflows',
  },
  {
    id: 'rag',
    title: 'RAG Systems',
    description:
      'AI that actually knows your business data. Custom knowledge bases, intelligent search, and document Q&A that give real answers.',
    visual: 'rag',
  },
  {
    id: 'strategy',
    title: 'AI Strategy',
    description:
      'Clear roadmaps for AI adoption. We audit your tech stack, identify high-impact opportunities, and build an implementation plan.',
    visual: 'strategy',
  },
];

export const approachSteps = [
  {
    number: '01',
    title: 'Discovery',
    description:
      'We learn your business inside and out \u2014 your workflows, pain points, and goals \u2014 to find the highest-impact AI opportunities.',
    meta: 'Typical: 2-3 calls + async audit',
  },
  {
    number: '02',
    title: 'Strategy',
    description:
      'We design the right solution with a clear roadmap, timeline, and ROI projection so you know exactly what to expect.',
    meta: 'Deliverable: Technical spec + ROI model',
  },
  {
    number: '03',
    title: 'Build',
    description:
      'We implement and integrate your AI solution with your existing tools and workflows. No disruption, just results.',
    meta: 'Weekly demos + iterative delivery',
  },
  {
    number: '04',
    title: 'Support',
    description:
      'Ongoing optimization, monitoring, and scaling to make sure your AI keeps delivering as your business grows.',
    meta: 'SLA-backed response times',
  },
];

export const serviceOptions = [
  { id: 'aios', label: 'AIOS (Flagship)', icon: 'Brain' },
  { id: 'agents', label: 'AI Agents', icon: 'Bot' },
  { id: 'workflows', label: 'n8n Workflows', icon: 'Workflow' },
  { id: 'rag', label: 'RAG Systems', icon: 'Database' },
  { id: 'strategy', label: 'AI Strategy', icon: 'Lightbulb' },
  { id: 'b2b-leadgen', label: 'B2B Lead Gen', icon: 'Target' },
];

export const timelineOptions = [
  { id: 'asap', label: 'ASAP', icon: 'Zap' },
  { id: '1-3', label: '1-3 months', icon: 'Clock' },
  { id: '3-6', label: '3-6 months', icon: 'Calendar' },
  { id: 'exploring', label: 'Exploring', icon: 'Compass' },
];

export const budgetOptions = [
  { id: 'under-5k', label: 'Under $5k' },
  { id: '5-15k', label: '$5k - $15k' },
  { id: '15-30k', label: '$15k - $30k' },
  { id: '30k+', label: '$30k+' },
];

export const expectations = [
  'Free 30-minute consultation',
  'Response within 24 hours',
  'No-obligation project scoping',
  'Transparent pricing upfront',
];

export const heroCode = [
  { n: 1, t: '// Configure your AI sales agent', c: 'comment' },
  { n: 2, t: "import { Agent, tools } from '@elevate-ai/sdk'", c: 'import' },
  { n: 3, t: '', c: 'blank' },
  { n: 4, t: 'export const agent = new Agent({', c: 'code' },
  { n: 5, t: "  name: 'LeadGen Pro',", c: 'string' },
  { n: 6, t: "  model: 'claude-opus-4-6',", c: 'string' },
  { n: 7, t: '  tools: [crm, enrichment, slack],', c: 'code' },
  { n: 8, t: "  trigger: 'new_lead',", c: 'string' },
  { n: 9, t: '  actions: {', c: 'code' },
  { n: 10, t: '    enrich: true,', c: 'code' },
  { n: 11, t: '    scoreLeads: true,', c: 'code' },
  { n: 12, t: "    notifyTeam: '#sales'", c: 'string' },
  { n: 13, t: '  }', c: 'code' },
  { n: 14, t: '})', c: 'code' },
  { n: 15, t: '', c: 'blank' },
  { n: 16, t: '// \ud83d\ude80 12 leads enriched, 3 meetings booked today', c: 'comment' },
];

// ---------------- AIOS page content (original copy) ----------------
export const aiosHero = {
  badge: 'Institutional Intelligence \u00B7 Owned By Your Team',
  eyebrow: 'AI Operating System',
  title: 'Become an AI-first company with your own',
  titleAccent: 'Operating System.',
  description:
    'Your team is using AI every day. Your company has nothing to show for it. We install the shared layer that turns scattered personal productivity into compounding institutional intelligence \u2014 wired into your existing stack and handed back to your team in 14 days.',
  primaryCta: { label: 'Start the 14-day install', href: '/#contact' },
};

export const aiosGap = {
  eyebrow: 'Section 01 \u00B7 The Gap',
  title: 'Your team has AI. Your company has no memory.',
  description:
    'Knowledge hides in Slack DMs, threaded emails, half-finished docs, CRM notes, and the heads of three senior people. Every prompt rediscovers what the business already learned last quarter. The companies pulling ahead in 2025 stopped letting that happen.',
  sources: ['Gmail', 'Drive', 'Slack', 'Notion', 'CRM', 'Calendar', 'SOPs', '+ more'],
  timeline: [
    { label: 'Day 14', text: 'Context, memory, and the first agents are live.' },
    { label: 'Month 1', text: 'Every decision gets logged. Outputs start to compound.' },
    { label: 'Month 3', text: 'Your playbook begins running itself.' },
    { label: 'Month 6', text: 'Institutional intelligence outperforms any single hire.' },
    { label: 'Year 1', text: 'Competitors are 12 months behind \u2014 on intelligence, not tools.' },
  ],
  footer:
    'Scattered context stays flat. Institutional intelligence compounds. The gap widens every month you postpone.',
};

export const aiosInstalled = {
  eyebrow: 'Section 02 \u00B7 What Gets Installed',
  title: 'Two deliverables. One backbone.',
  description:
    'A working Second Brain wired into your stack, plus two proof automations running on top of it. Both yours on day one of handoff \u2014 no monthly seat fees, no vendor lock-in.',
  deliverables: [
    {
      number: '01',
      label: 'context \u00B7 queryable \u00B7 team-owned',
      title: 'A working Second Brain.',
      description:
        "Every AI interaction across your team reads from one shared place: customer history, SOPs, brand voice, deal patterns, commercial rules. The institutional knowledge that used to live in ten people's heads \u2014 now searchable by every person and every automation. Plugs into your existing tools without replacing anything.",
      gets: [
        'One source of truth for every AI prompt and agent',
        'Auto-ingestion from calls, email, docs, and CRM',
        'Wires into your existing CRM, calendar, and chat',
      ],
      handoff: [
        'Your internal Operator trained to extend it',
        'System owned by your team \u2014 not us',
      ],
    },
    {
      number: '02',
      label: 'scheduled \u00B7 autonomous \u00B7 a pattern for the next ten',
      title: 'Two proof automations.',
      description:
        "End-to-end automations built on top of the Brain in week two. Chosen from the highest-leverage repetitive workflows we observe in week one. They run on real systems, unprompted \u2014 and they become the template your Operator clones for everything else.",
      gets: [
        'Inbound call \u2192 CRM update + follow-up tasks',
        'Weekly ops digest auto-sent to leadership',
        'Deal qualification and routing on autopilot',
      ],
      handoff: [
        'One proves the Brain works on live data',
        'One proves the pattern is repeatable',
      ],
    },
  ],
};

export const aiosPhases = {
  eyebrow: 'Section 03 \u00B7 The 14 Days',
  title: 'Three phases. Two weeks. One handoff.',
  description:
    'Every engagement runs the same playbook. By day 14 the system is live, the proof automations are running, and your internal Operator is the one driving it forward.',
  phases: [
    {
      number: '01',
      days: 'Days 1\u20134',
      title: 'Discovery',
      description:
        'Live screen-share sessions watching the business actually run \u2014 not how the SOPs say it runs. Real workflows, real friction, real handoffs between tools and people.',
      output: [
        'Operational map grounded in observation',
        'Two proof automations scoped and confirmed',
      ],
    },
    {
      number: '02',
      days: 'Days 5\u201310',
      title: 'Brain Setup',
      description:
        'We build the Second Brain itself: project structure, knowledge base, mastermind database, modular connectors, agent scaffolding. The substrate every future automation runs on.',
      output: [
        'Centralized context layer, populated and wired',
        'Connector layer live across your stack',
      ],
    },
    {
      number: '03',
      days: 'Days 11\u201314',
      title: 'Proof + Handoff',
      description:
        'Two end-to-end automations shipped on top of the Brain. Live team workshop. Operator trained on extension patterns so building continues without us in the room.',
      output: [
        'Two proof automations running on real data',
        'System fully owned by your team',
      ],
    },
  ],
};

export const aiosOperator = {
  eyebrow: 'Section 04 \u00B7 The Operator',
  title: 'The person who runs it after we leave.',
  description:
    'Every successful implementation has one thing in common \u2014 an internal owner. Not a new full-time hire. A few hours a week from someone already on your team. We pick them with you in week one and train them in parallel with the build.',
  roles: [
    {
      number: '01',
      title: 'Maintains the Brain',
      description:
        'Updates context as strategy shifts, products change, people join. The Brain stays current so every output stays accurate. Minutes a day, not hours.',
    },
    {
      number: '02',
      title: 'Ships new automations',
      description:
        'Takes the patterns from the two proof builds and clones them onto the next workflow. And the next. The Brain compounds because the Operator keeps building.',
    },
    {
      number: '03',
      title: 'Onboards teammates',
      description:
        'Shows new hires how the Brain works on day one. Adoption stops being a leadership problem and becomes a built-in onboarding step.',
    },
    {
      number: '04',
      title: 'Internal point of contact',
      description:
        'When a teammate has an AI question, the Operator is the first stop \u2014 not IT, not the CEO. Real position, not a side gig.',
    },
  ],
};

export const aiosCta = {
  eyebrow: 'Ready',
  title: 'Install your Second Brain.',
  description:
    '14 days from kickoff to a working system your team owns. One call to scope it. One handoff to run it.',
  cta: { label: 'Start the 14-day install', href: '/#contact' },
  subline: 'Straight to a real call. No funnel. No email gauntlet.',
};

// Featured AIOS card shown above the services grid on the home page
export const aiosFeatured = {
  eyebrow: 'Flagship offering',
  title: 'AIOS \u2014 your AI Operating System, installed in 14 days.',
  description:
    'A shared Second Brain wired into your stack, plus the automations that run on top of it. Owned by your team on day one of handoff \u2014 no monthly seat fees, no vendor lock-in.',
  bullets: [
    'Centralized context across CRM, email, docs, and calls',
    'Live automations running on real data by day 14',
    'Internal Operator trained \u2014 your team owns it',
  ],
  ctaLabel: 'See the full system',
  ctaHref: '/aios',
};

// Testimonials shown on the AIOS page (placeholder social proof)
export const aiosTestimonials = [
  {
    quote:
      'We freed up 12 hours a week of sales ops within the first month. The Brain is now the first place every new hire goes.',
    author: 'VP Revenue Ops',
    company: 'B2B SaaS, Series A',
  },
  {
    quote:
      'Three weeks in we shipped two more automations ourselves. That was the whole point \u2014 we own it.',
    author: 'Head of Operations',
    company: 'Mid-market services',
  },
];
